import React from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { useVersionCheck } from '../hooks/useVersionCheck';

interface MobileUpdateNotificationProps {
  variant?: 'banner' | 'modal';
  position?: 'top' | 'bottom';
  className?: string;
}

const MobileUpdateNotification: React.FC<MobileUpdateNotificationProps> = ({ 
  variant = 'banner', 
  position = 'top',
  className = ''
}) => {
  const { hasUpdate, currentVersion, latestVersion, handleUpdate, dismissUpdate } = useVersionCheck();

  if (!hasUpdate) return null;

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 md:hidden">
        <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-4">
          <div className="flex items-center mb-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-primary" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Yeni Güncelleme!</h3>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Uygulamanın yeni bir versiyonu yayınlandı. En iyi deneyim için güncelleyin.
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Mevcut: {currentVersion}</div>
              <div>Yeni: {latestVersion}</div>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={dismissUpdate}
              className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Daha Sonra
            </button>
            <button
              onClick={handleUpdate}
              className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Güncelle
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Banner variant
  const positionClasses = position === 'top' 
    ? "fixed top-0 left-0 right-0 z-50 md:hidden" 
    : "fixed bottom-0 left-0 right-0 z-50 md:hidden";

  return (
    <div className={`${positionClasses} bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-primary/30 shadow-lg ${className}`}>
      <div className="p-3">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-primary-dark">Yeni Güncelleme Mevcut!</h3>
            <p className="text-xs text-primary-dark mt-1">
              Uygulamanın yeni bir versiyonu yayınlandı.
            </p>
            <div className="text-xs text-primary mt-1">
              <div>Mevcut: {currentVersion}</div>
              <div>Yeni: {latestVersion}</div>
            </div>
          </div>
          <div className="flex flex-col gap-1 ml-2">
            <button
              onClick={handleUpdate}
              className="bg-primary text-white px-2 py-1 rounded text-xs hover:bg-primary-dark transition-colors flex items-center"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Güncelle
            </button>
            <button
              onClick={dismissUpdate}
              className="text-primary hover:text-primary-dark text-xs px-2 py-1 rounded hover:bg-primary/20 transition-colors"
            >
              Daha Sonra
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileUpdateNotification;
